import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router"
import HTTPService from "../../../App/HTTPService"
import { CallerComponent, StorageKey } from "../../../App/enums"
import { organizationProfileDefault, profileDefault } from "../../../App/GlobaleVariables"
import { formatDate } from "../../../App/Helpers/Helpers"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import SessionService from "../../../App/SessionService"
import MessageBody from "../../Shared/Components/MessageBody"
import BackgroundWrapper from "../../Shared/Components/BackgroundWrapper"
import Footer from "../../Shared/Layout/Footer"
import Image from "../../Shared/Primitive/Image"
import Button from "../../Shared/Primitive/Button"
import {
    EventsTabIcon,
    LoadingAnimation,
    LogoIconMobile,
    MediaTabIcon,
    MembersTabIcon,
    PostsCommentsTabIcon,
    PostsTabIcon,
    SearchIcon,
} from "../../Shared/Components/SvgIcons"

type PreviewPhoto = { id?: number; url_path?: string }

type PreviewActor = {
    id: number
    type: string
    first_name?: string
    last_name?: string
    name?: string
    profile_photo?: PreviewPhoto
    photo?: PreviewPhoto
}

type PreviewRoom = {
    id: number
    name: string
    type: string
    photo?: string
    description: string
    organization?: { id: number; name: string; photo?: PreviewPhoto }
    room_managers: PreviewActor[]
    room_moderators: PreviewActor[]
    hashtags: string[]
    total_members: number
    total_events: number
}

type PreviewPost = {
    id: number
    content: string
    date: string
    event_title?: string
    event_photo?: string
    is_event?: boolean
    post_comments: PreviewComment[]
    hashtags: string[]
    show_preview: boolean
    author?: PreviewActor
}

type PreviewComment = {
    id: number
    content: string
    date: string
    hashtags: string[]
    show_preview: boolean
}

type PreviewEvent = {
    id: number
    title: string
    description: string
    start_date: string
    end_date: string
    location_type?: string
    location_details?: string
    image?: string
}

type PaginatedResponse<T> = {
    next: string | null
    previous: string | null
    results: T[]
}

type PreviewTab = "posts" | "events"

const blockedTabs = ["members", "new-post", "media"]

const getPreviewActorName = (person: PreviewActor, fallbackLabel: string) => {
    const fullName = [person.first_name, person.last_name].filter(Boolean).join(" ").trim()
    return person.name || fullName || fallbackLabel
}

const getEventDateRangeLabel = (event: PreviewEvent) => {
    const startDate = formatDate(event.start_date, "dd.MM.yyyy HH:mm")

    if (!startDate) {
        return event.start_date
    }

    if (!event.end_date || event.end_date === event.start_date) {
        return startDate
    }

    const endDate = formatDate(event.end_date, "dd.MM.yyyy HH:mm")
    return endDate ? `${startDate} - ${endDate}` : startDate
}

function PublicRoomPreviewScreen() {
    const translation = useSelector(selectTranslations)
    const navigate = useNavigate()
    const { roomId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const currentTab = (searchParams.get("tab") === "events" ? "events" : "posts") as PreviewTab

    const [room, setRoom] = useState<PreviewRoom | null>(null)
    const [posts, setPosts] = useState<PreviewPost[]>([])
    const [events, setEvents] = useState<PreviewEvent[]>([])
    const [postsNext, setPostsNext] = useState<string | null>(null)
    const [postsLoading, setPostsLoading] = useState(true)
    const [eventsLoading, setEventsLoading] = useState(true)
    const [roomLoading, setRoomLoading] = useState(true)
    const [screenError, setScreenError] = useState(false)
    const [registrationPromptOpen, setRegistrationPromptOpen] = useState(false)

    const heroImageSrc = "/edusiia_placeholder_room_picture.png"
    const heroImageClassName = `public-room-preview__hero-image${room?.photo ? "" : " public-room-preview__hero-image--placeholder"}`

    const uniqueVisiblePeople = useMemo(() => {
        const seen = new Set<string>()
        const merged = [...(room?.room_managers ?? []), ...(room?.room_moderators ?? [])]

        return merged.filter((person) => {
            const key = `${person.type}-${person.id}`
            if (seen.has(key)) {
                return false
            }
            seen.add(key)
            return true
        })
    }, [room?.room_managers, room?.room_moderators])

    const getPreviewRedirectUrl = useCallback(() => {
        return roomId ? `/room/${roomId}?previewJoin=1` : "/dashboard"
    }, [roomId])

    const persistPreviewJoinRoom = useCallback(() => {
        if (roomId) {
            SessionService.setItem(StorageKey.previewRoomJoinId, roomId)
        }
    }, [roomId])

    const navigateToSignIn = useCallback(() => {
        persistPreviewJoinRoom()
        navigate(`/signin?redirectUrl=${getPreviewRedirectUrl()}`)
    }, [getPreviewRedirectUrl, navigate, persistPreviewJoinRoom])

    useEffect(() => {
        if (!roomId) {
            setScreenError(true)
            setRoomLoading(false)
            setPostsLoading(false)
            setEventsLoading(false)
            return
        }

        setRoomLoading(true)
        setPostsLoading(true)
        setEventsLoading(true)
        setScreenError(false)

        HTTPService.get(`/room-preview/${roomId}/`)
            .then((response) => {
                setRoom(response.data)
                setRoomLoading(false)
            })
            .catch((error) => {
                if (error.response?.status === 403) {
                    navigateToSignIn()
                    return
                }

                setScreenError(true)
                setRoomLoading(false)
            })

        HTTPService.get(`/room-preview/${roomId}/posts/?page=1`)
            .then((response) => {
                const data = response.data as PaginatedResponse<PreviewPost>
                setPosts(data.results)
                setPostsNext(data.next)
                setPostsLoading(false)
            })
            .catch((error) => {
                if (error.response?.status === 403) {
                    navigateToSignIn()
                    return
                }

                setPostsLoading(false)
            })

        HTTPService.get(`/room-preview/${roomId}/events/?page=1&query=`)
            .then((response) => {
                const data = response.data as PaginatedResponse<PreviewEvent>
                setEvents(data.results)
                setEventsLoading(false)
            })
            .catch((error) => {
                if (error.response?.status === 403) {
                    navigateToSignIn()
                    return
                }

                setEventsLoading(false)
            })
    }, [navigateToSignIn, roomId])

    const openRegistrationPrompt = () => {
        setRegistrationPromptOpen(true)
    }

    const handleTabSelection = (tab: string) => {
        if (blockedTabs.includes(tab)) {
            openRegistrationPrompt()
            return
        }

        const nextParams = new URLSearchParams(searchParams)
        nextParams.set("tab", tab)
        setSearchParams(nextParams, { replace: true })
    }

    const navigateToSignUp = () => {
        persistPreviewJoinRoom()
        navigate(`/signup?redirectUrl=${getPreviewRedirectUrl()}`)
    }

    if (roomLoading) {
        return (
            <BackgroundWrapper>
                <div className="public-room-preview public-room-preview--loading">
                    <LoadingAnimation />
                </div>
            </BackgroundWrapper>
        )
    }

    if (screenError || !room) {
        return (
            <BackgroundWrapper>
                <div className="public-room-preview public-room-preview--empty-state">
                    <h1>{translation.roomPreviewNotFoundTitle ?? "Raumvorschau nicht verfügbar"}</h1>
                    <p>{translation.roomPreviewNotFoundText ?? "Dieser Raum ist aktuell nicht öffentlich verfügbar."}</p>
                    <div className="public-room-preview__hero-actions">
                        <Button className="button button--medium button--border-radius-05 button--cursor-pointer button--hover-brighter button--color-green button--text-color-white" onClick={navigateToSignIn}>
                            {translation.signin}
                        </Button>
                    </div>
                </div>
            </BackgroundWrapper>
        )
    }

    return (
        <BackgroundWrapper>
            <>
                <div className="public-room-preview">
                    <div className="public-room-preview__platform-header">
                        <div className="public-room-preview__platform-header-left">
                            <div className="public-room-preview__brand">
                                <LogoIconMobile />
                            </div>
                            <div className="public-room-preview__eyebrow">{translation.roomPreviewEyebrow ?? "Raumvorschau"}</div>
                        </div>
                        <button type="button" className="public-room-preview__platform-search" onClick={openRegistrationPrompt}>
                            <SearchIcon color="dark-blue" />
                            <input
                                type="text"
                                readOnly
                                tabIndex={-1}
                                value=""
                                placeholder={translation.roomPreviewSearchPlaceholder ?? "Nach weiteren Räumen, Themen, Personen & Organisationen suchen"}
                            />
                        </button>
                        <div className="public-room-preview__platform-menu">
                            <button type="button" className="public-room-preview__platform-menu-item" onClick={openRegistrationPrompt}>
                                <span>{translation.roomPreviewMenuHome ?? "Start"}</span>
                            </button>
                            <button type="button" className="public-room-preview__platform-menu-item" onClick={openRegistrationPrompt}>
                                <span>{translation.roomPreviewMenuChat ?? "Chat"}</span>
                            </button>
                            <button type="button" className="public-room-preview__platform-menu-item" onClick={openRegistrationPrompt}>
                                <span>{translation.roomPreviewMenuNotifications ?? "Benachrichtigungen"}</span>
                            </button>
                            <button type="button" className="public-room-preview__platform-menu-item" onClick={openRegistrationPrompt}>
                                <span>{translation.roomPreviewMenuHelp ?? "Hilfe"}</span>
                            </button>
                        </div>
                    </div>
                    <div className="public-room-preview__hero">
                        <div className="public-room-preview__hero-image-column">
                            <Image 
                                src={heroImageSrc}
                                alt={room.name} 
                                className={heroImageClassName}
                            />
                        </div>
                        <div className="public-room-preview__hero-content">
                            <h1 className="public-room-preview__title">{room.name}</h1>
                            <p className="public-room-preview__subtitle">
                                {translation.roomPreviewSubtitle ?? "Du kannst diesen Raum schon vor der Registrierung ansehen. Für Interaktionen und weitere Bereiche brauchst du ein Konto."}
                            </p>
                            <div className="public-room-preview__meta-row">
                                <span className="public-room-preview__meta-pill">{room.type === "private" ? translation.private : translation.public}</span>
                                <span className="public-room-preview__meta-pill">{room.total_members} {translation.members}</span>
                                <span className="public-room-preview__meta-pill">{room.total_events} {translation.events}</span>
                            </div>
                            <div className="public-room-preview__hero-actions">
                                <Button className="button button--medium button--border-radius-05 button--cursor-pointer button--hover-brighter button--color-green button--text-color-white" onClick={navigateToSignUp}>
                                    {translation.signup}
                                </Button>
                                <Button className="button button--medium button--border-radius-05 button--cursor-pointer button--hover-brighter button--color-white button--text-color-dark-blue" onClick={navigateToSignIn}>
                                    {translation.signin}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="public-room-preview__layout">
                        <aside className="public-room-preview__sidebar">
                            <section className="public-room-preview__card">
                                <h2 className="public-room-preview__section-title">{translation.description}</h2>
                                <MessageBody
                                    caller={CallerComponent.roomDescription}
                                    text={room.description}
                                    showPreview={false}
                                    showCollapsed={false}
                                    showRemovePreviewIcon={false}
                                    hashtags={room.hashtags}
                                    isCollapsed={false}
                                />
                            </section>

                            <section className="public-room-preview__card">
                                <h2 className="public-room-preview__section-title">{translation.roomCreator}</h2>
                                <div className="public-room-preview__person-card">
                                    <Image
                                        src={organizationProfileDefault}
                                        alt={translation.roomCreator}
                                        className="public-room-preview__person-image"
                                    />
                                    <div>
                                        <div className="public-room-preview__person-name">{room.organization?.name}</div>
                                        <div className="public-room-preview__person-role">{translation.roomCreator}</div>
                                    </div>
                                </div>
                            </section>

                            <section className="public-room-preview__card">
                                <h2 className="public-room-preview__section-title">{translation.roomPreviewModerator ?? "Gastgeber:in & Moderation"}</h2>
                                <div className="public-room-preview__people-list">
                                    {uniqueVisiblePeople.map((person) => {
                                        const imageSrc = person.type === "user" ? profileDefault : organizationProfileDefault
                                        const isModerator = room.room_moderators.some((moderator) => moderator.id === person.id && moderator.type === person.type)
                                        const roleLabel = isModerator
                                            ? translation.roomPreviewModerator ?? "Moderator:in"
                                            : translation.roomManager
                                        const label = getPreviewActorName(person, roleLabel)

                                        return (
                                            <div className="public-room-preview__person-card" key={`${person.type}-${person.id}`}>
                                                <Image src={imageSrc} alt={roleLabel} className="public-room-preview__person-image" />
                                                <div>
                                                    <div className="public-room-preview__person-name">{label}</div>
                                                    <div className="public-room-preview__person-role">{roleLabel}</div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </section>
                        </aside>

                        <main className="public-room-preview__content">
                            <div className="public-room-preview__panel">
                                {currentTab === "posts" ? (
                                    <>
                                        {posts.map((post) => (
                                            <article className="public-room-preview__post-card" key={post.id}>
                                                <div className="public-room-preview__post-wrapper">
                                                    {/* Author Avatar */}
                                                    <div className="public-room-preview__post-avatar">
                                                        {post.author && (
                                                            <Image
                                                                src={post.author.type === "user" ? profileDefault : organizationProfileDefault}
                                                                alt={getPreviewActorName(post.author, "Author")}
                                                                className="public-room-preview__post-author-image"
                                                            />
                                                        )}
                                                    </div>
                                                    {/* Post Content */}
                                                    <div className="public-room-preview__post-content-wrapper">
                                                        <div className="public-room-preview__post-header">
                                                            <div className="public-room-preview__post-label">{translation.roomPreviewPostLabel ?? "Beitrag aus dem Raum"}</div>
                                                            <div className="public-room-preview__post-date">{formatDate(post.date, "HH:mm dd.MM.yyyy") ?? post.date}</div>
                                                        </div>
                                                        {post.is_event && post.event_title && (
                                                            <div className="public-room-preview__embedded-event">{post.event_title}</div>
                                                        )}
                                                        <MessageBody
                                                            caller={CallerComponent.post}
                                                            id={post.id}
                                                            showPreview={post.show_preview}
                                                            text={post.content}
                                                            showRemovePreviewIcon={false}
                                                            isCollapsed={false}
                                                            toggleCollapse={() => undefined}
                                                            showCollapsed={false}
                                                            hashtags={post.hashtags}
                                                        />
                                                        {post.post_comments.length > 0 && (
                                                            <div className="public-room-preview__comments">
                                                                {post.post_comments.map((comment) => (
                                                                    <div className="public-room-preview__comment-card" key={comment.id}>
                                                                        <div className="public-room-preview__comment-wrapper">
                                                                            <div className="public-room-preview__comment-avatar">
                                                                                <Image
                                                                                    src={profileDefault}
                                                                                    alt={translation.comment ?? "Kommentar"}
                                                                                    className="public-room-preview__comment-author-image"
                                                                                />
                                                                            </div>
                                                                            <div className="public-room-preview__comment-content-wrapper">
                                                                                <div className="public-room-preview__comment-header">
                                                                                    <div className="public-room-preview__comment-label">{`${translation.comment ?? "Kommentar"}`}</div>
                                                                                    <div className="public-room-preview__comment-date">{formatDate(comment.date, "HH:mm dd.MM.yyyy") ?? comment.date}</div>
                                                                                </div>
                                                                                <MessageBody
                                                                                    caller={CallerComponent.post}
                                                                                    id={comment.id}
                                                                                    showPreview={comment.show_preview}
                                                                                    text={comment.content}
                                                                                    showRemovePreviewIcon={false}
                                                                                    isCollapsed={false}
                                                                                    toggleCollapse={() => undefined}
                                                                                    showCollapsed={false}
                                                                                    hashtags={comment.hashtags}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </article>
                                        ))}

                                        {postsLoading && <LoadingAnimation type="post" />}

                                        {!postsLoading && posts.length === 0 && (
                                            <div className="public-room-preview__empty-copy">
                                                {translation.roomPreviewNoPosts ?? "Für diese Vorschau stehen aktuell keine Beiträge bereit."}
                                            </div>
                                        )}

                                        {!postsLoading && postsNext && (
                                            <div className="public-room-preview__posts-limit">
                                                <h3 className="public-room-preview__posts-limit-title">
                                                    {translation.roomPreviewRegistrationRequired ?? "Bitte registriere dich zuerst"}
                                                </h3>
                                                <p className="public-room-preview__posts-limit-copy">
                                                    {translation.roomPreviewMorePostsHint ?? "Du siehst hier nur die letzten 5 Beiträge. Für weitere Inhalte melde dich an oder registriere dich."}
                                                </p>
                                                <div className="public-room-preview__posts-limit-actions">
                                                    <Button
                                                        className="button button--medium button--border-radius-05 button--cursor-pointer button--hover-brighter button--color-green button--text-color-white"
                                                        onClick={navigateToSignUp}
                                                    >
                                                        {translation.signup}
                                                    </Button>
                                                    <Button
                                                        className="button button--medium button--border-radius-05 button--cursor-pointer button--hover-brighter button--color-white button--text-color-dark-blue"
                                                        onClick={navigateToSignIn}
                                                    >
                                                        {translation.signin}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {eventsLoading && <LoadingAnimation type="post" />}

                                        {!eventsLoading && events.length === 0 && (
                                            <div className="public-room-preview__events-empty">
                                                <div className="public-room-preview__events-empty-icon">
                                                    <EventsTabIcon />
                                                </div>
                                                <h3 className="public-room-preview__events-empty-title">
                                                    {translation.roomEventsEmptyTitle ?? "Noch keine Events in diesem Raum"}
                                                </h3>
                                            </div>
                                        )}

                                        {!eventsLoading && events.map((event) => (
                                            <button className="public-room-preview__event-card" key={event.id} onClick={openRegistrationPrompt}>
                                                {event.image && <Image src={event.image} alt={event.title} className="public-room-preview__event-image" />}
                                                <div className="public-room-preview__event-content">
                                                    <div className="public-room-preview__event-title">{event.title}</div>
                                                    <div className="public-room-preview__event-date">{getEventDateRangeLabel(event)}</div>
                                                    <div className="public-room-preview__event-description" dangerouslySetInnerHTML={{ __html: event.description }} />
                                                </div>
                                            </button>
                                        ))}
                                    </>
                                )}
                            </div>
                            <div className="public-room-preview__tabs">
                                <button
                                    className={`public-room-preview__tab ${currentTab === "posts" ? "public-room-preview__tab--active" : ""}`}
                                    onClick={() => handleTabSelection("posts")}
                                    aria-label={translation.exchange ?? "Austausch"}
                                >
                                    <PostsTabIcon />
                                    <span>{translation.exchange ?? "Austausch"}</span>
                                </button>
                                <button
                                    className="public-room-preview__tab public-room-preview__tab--locked"
                                    onClick={() => handleTabSelection("members")}
                                    aria-label={translation.members}
                                >
                                    <MembersTabIcon />
                                    <span>{translation.members}</span>
                                </button>
                                <button
                                    className="public-room-preview__tab public-room-preview__tab--locked"
                                    onClick={() => handleTabSelection("new-post")}
                                    aria-label={translation.post ?? "Posten"}
                                >
                                    <PostsCommentsTabIcon />
                                    <span>{translation.post ?? "Posten"}</span>
                                </button>
                                <button
                                    className="public-room-preview__tab public-room-preview__tab--locked"
                                    onClick={() => handleTabSelection("media")}
                                    aria-label={translation.mediaLibrary ?? "Mediathek"}
                                >
                                    <MediaTabIcon />
                                    <span>{translation.mediaLibrary ?? "Mediathek"}</span>
                                </button>
                                <button
                                    className={`public-room-preview__tab ${currentTab === "events" ? "public-room-preview__tab--active" : ""}`}
                                    onClick={() => handleTabSelection("events")}
                                    aria-label={translation.events}
                                >
                                    <EventsTabIcon />
                                    <span>{translation.events}</span>
                                </button>
                            </div>
                        </main>
                    </div>

                    {registrationPromptOpen && (
                        <div className="public-room-preview__modal-backdrop" onClick={() => setRegistrationPromptOpen(false)}>
                            <div className="public-room-preview__modal" onClick={(event) => event.stopPropagation()}>
                                <button
                                    type="button"
                                    className="public-room-preview__modal-close"
                                    aria-label="Modal schließen"
                                    onClick={() => setRegistrationPromptOpen(false)}
                                >
                                    x
                                </button>
                                <div className="public-room-preview__modal-title">{translation.roomPreviewRegistrationRequired ?? "Bitte registriere dich zuerst"}</div>
                                <p className="public-room-preview__modal-text">
                                    {translation.roomPreviewRegistrationHint ?? "Du kannst diesen Raum bereits ansehen. Für Mitgliederliste, Mediathek, eigene Beiträge und Event-Details brauchst du ein edusiia Konto."}
                                </p>
                                <div className="public-room-preview__modal-actions">
                                    <Button className="button button--medium button--border-radius-05 button--cursor-pointer button--hover-brighter button--color-green button--text-color-white" onClick={navigateToSignIn}>
                                        {translation.signin}
                                    </Button>
                                    <Button className="button button--medium button--border-radius-05 button--cursor-pointer button--hover-brighter button--color-green button--text-color-white" onClick={navigateToSignUp}>
                                        {translation.signup}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <Footer />
            </>
        </BackgroundWrapper>
    )
}

export default PublicRoomPreviewScreen